import React, {useState} from 'react';

import {Button, ModalPopup} from '@kit';

import {Ability} from '../../../../generated/types';
import {useCharacter} from '../character-context';
import type {HitDice} from '../model/character';
import {dieFormula, formatDice} from '../model/dice';
import {withJournalEntry} from '../model/journal';
import {longRestPatch, shortRestPatch} from '../model/rests';
import {useRoll} from './dice-roller';
import {GlossaryDescription} from './expandable-row';
import {ShortRestFeatureActions} from './feature-actions/rest-feature-actions';
import {LongRestChanges} from './rest/long-rest-changes';

import css from './sheet.module.css';

type Props = {
	onClose(): void;
};

export const ShortRestModal: React.FC<Props> = ({onClose}) => {
	const {character, update} = useCharacter();
	const roll = useRoll();
	const constitution = character.abilityModifier(Ability.Constitution);
	const isHealthy = character.currentHitPoints >= character.maxHitPoints;
	/** пока кости летят, следующую Кость здоровья не бросить: ПЗ ещё не посчитаны */
	const [isRolling, setRolling] = useState(false);

	const spendHitDie = ({die, spent}: HitDice) => {
		update((current) => ({spentHitDice: {...current.spentHitDice, [die]: spent + 1}}));
		setRolling(true);
		roll({
			title: `Кость здоровья d${die}`,
			formula: dieFormula(die, constitution),
			onTotal: (total) => {
				setRolling(false);
				update((current) => ({
					hitPoints: {...current.hitPoints, spent: Math.max(0, current.hitPoints.spent - Math.max(0, total))},
				}));
			},
		});
	};

	const finishRest = () => {
		update((current) => ({
			...shortRestPatch(character, current),
			...withJournalEntry(current, {kind: 'rest', title: 'Короткий отдых'}),
		}));
		onClose();
	};

	return (
		<ModalPopup open onClose={onClose} header="Короткий отдых" maxWidth={640}>
			<GlossaryDescription id="short-rest" className={css.restArticle} />
			<h3 className={css.groupTitle}>
				Кости здоровья{' '}
				<span className={css.muted}>
					ПЗ {character.currentHitPoints} / {character.maxHitPoints}
				</span>
			</h3>
			{isHealthy && <p className={css.muted}>ПЗ полные, кости здоровья тратить не нужно.</p>}
			<ul className={css.rows}>
				{character.hitDice.map((hitDice) => {
					const available = hitDice.count - hitDice.spent;
					return (
						<li key={hitDice.die} className={css.row}>
							<div className={css.rowMain}>
								<span className={css.rowTitle}>
									d{hitDice.die}: доступно {available} из {hitDice.count}
								</span>
								<Button
									disabled={!available || isHealthy || isRolling}
									onClick={() => spendHitDie(hitDice)}
								>
									Бросить {formatDice(dieFormula(hitDice.die, constitution))}
								</Button>
							</div>
						</li>
					);
				})}
			</ul>
			<ShortRestFeatureActions />
			<div className={css.modalButtons}>
				<Button appearance="primary" onClick={finishRest}>
					Совершить короткий отдых
				</Button>
			</div>
		</ModalPopup>
	);
};

export const LongRestModal: React.FC<Props> = ({onClose}) => {
	const {character, update} = useCharacter();

	const finishRest = () => {
		update((current) => ({
			...longRestPatch(character, current),
			...withJournalEntry(current, {kind: 'rest', title: 'Долгий отдых'}),
		}));
		onClose();
	};

	return (
		<ModalPopup open onClose={onClose} header="Долгий отдых" maxWidth={640}>
			<GlossaryDescription id="long-rest" className={css.restArticle} />
			<LongRestChanges />
			<div className={css.modalButtons}>
				<Button appearance="primary" onClick={finishRest}>
					Долгий отдых
				</Button>
			</div>
		</ModalPopup>
	);
};
